// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTrackCreation } from "../../../../../context/TrackCreationContext";
import { useTranslation } from "react-i18next";
import { DropdownOption, QuizLanguageProps, RouteParamsWithLanguage } from "../../../../../types/screensTypes";
import Button from "../../../../../components/ui/Button";
import Dropdown from "../../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../../components/common/TitleSubtitle";
import { saveQuiz, getQuizLanguages } from "../../../../../api";
import { useGlobalState } from "../../../../../context/useGlobalState";
import stepStyles from "../../createStep.module.css";

const QuizLanguage: React.FC<QuizLanguageProps> = ({ navigateToNext }) => {
    const { quizId } = useParams<RouteParamsWithLanguage>(); // Get quizId from the URL
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data, loading, error } = useGlobalState();
    const { trackData } = useTrackCreation();

    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
    const [existingLanguages, setExistingLanguages] = useState<string[]>([]);
    const [showAddLanguageDropdown, setShowAddLanguageDropdown] = useState<boolean>(false);

    // Save quiz language selection
    const saveQuizAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !quizId || !selectedLanguage) return;
        await saveQuiz({
            token,
            trackId,
            quizId,
            language: selectedLanguage,
        });
        navigateToNext(quizId, selectedLanguage);
    };

    // Fetch languages already used for the quiz
    const fetchExistingLanguages = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !quizId) return;
        const languages = await getQuizLanguages({ token, quizId });
        const languagesArray = languages.map((lang: { id: string }) => lang.id);
        setExistingLanguages(languagesArray);
    };

    useEffect(() => {
        if (quizId && quizId !== "") {
            fetchExistingLanguages();
        }
    }, [quizId]);

    const languagesFiltered: DropdownOption[] = [
        ...((data.languagesAI as DropdownOption[] | undefined)?.filter((lang) =>
            existingLanguages.includes(lang.value),
        ) || []),
        { label: t("addANewLanguage"), value: "add-new-language" },
    ];

    const languagesUnfiltered: DropdownOption[] =
        (data.languagesAI as DropdownOption[] | undefined)?.filter((lang) => !existingLanguages.includes(lang.value)) ||
        [];

    const handleLanguageChange = (selectedOption: string): void => {
        if (selectedOption === "add-new-language") {
            setShowAddLanguageDropdown(true);
            setSelectedLanguage(null);
        } else {
            setShowAddLanguageDropdown(false);
            setSelectedLanguage(selectedOption);
        }
    };

    return (
        <div className={`${stepStyles.editScreen} ${stepStyles.quizEditScreen}`}>
            <TitleSubtitle title={t("selectANewLanguage")} subtitle={""} layout="edit" />
            <h5>{t("existingLanguages")}</h5>
            <Dropdown
                label=""
                options={languagesFiltered}
                onChange={handleLanguageChange}
                loading={loading}
                error={error ? true : false}
                placeholder={t("language")}
                value={selectedLanguage || ""}
            />
            {showAddLanguageDropdown && (
                <>
                    <h5>{t("selectANewLanguage")}</h5>
                    <Dropdown
                        label=""
                        options={languagesUnfiltered}
                        onChange={(value: string) => setSelectedLanguage(value)}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("language")}
                        value={selectedLanguage || ""}
                    />
                </>
            )}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={saveQuizAPI}
                />
            </div>
        </div>
    );
};

export default QuizLanguage;
